import React from "react";
import styled from "styled-components";
import { Link, graphql } from "gatsby";
import Layout from "../components/Layout";
import { GlobalStyle } from "./HomePage";
import Adsense from "react-adsense";
import ReactGA from "react-ga";
import Moment from "moment";
import { optimizeImageUrl } from "../lib/prismicImage";
ReactGA.initialize("UA-141669883-1");

/* ─── Styles ─── */

const PageHeader = styled.div`
  background: var(--bg);
  border-bottom: 1px solid var(--border);
  padding: 1.5rem 0;
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const BreadcrumbLink = styled(Link)`
  color: var(--link);
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
`;

const ArticleCount = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0.25rem 0 0;
`;

const PageBody = styled.section`
  padding: 2rem 0;
  background: var(--bg-secondary);
`;

const BodyInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (min-width: 640px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 1024px) { grid-template-columns: repeat(4, 1fr); }
`;

const ReviewCard = styled(Link)`
  display: block;
  text-decoration: none;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--card-bg);
  border: 1px solid var(--border);
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    transform: translateY(-2px);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 160px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  @media (min-width: 640px) { height: 180px; }
`;

const RatingBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(17, 24, 39, 0.85);
  color: #fbbf24;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
`;

const CardContent = styled.div`
  padding: 0.75rem;
`;

const CardTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--compact-title);
  margin: 0 0 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const CardDate = styled.p`
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
`;

const AdWrap = styled.div`
  width: 100%;
  margin: 1.5rem 0;
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: var(--text-muted);
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
`;

/* ─── Component ─── */

const ReviewItem = ({ node }) => (
    <ReviewCard to={`/${node.uid}/`}>
        <CardImage src={optimizeImageUrl(node.data.img ? node.data.img.url : '', 500)} />
        {node.data.rating ? <RatingBadge>★ {node.data.rating}</RatingBadge> : null}
        <CardContent>
            <CardTitle>{node.data.title ? node.data.title.text : 'Untitled'}</CardTitle>
            {node.data.datetime && (
                <CardDate>{Moment(node.data.datetime).format('DD MMM, YYYY')}</CardDate>
            )}
        </CardContent>
    </ReviewCard>
);

const ReviewsPage = ({ data }) => {
    ReactGA.pageview('/reviews');

    const articles = (data.allPrismicReviewarticle && data.allPrismicReviewarticle.edges) || [];

    return (
        <>
            <GlobalStyle />
            <Layout>
                <PageHeader>
                    <HeaderInner>
                        <Breadcrumb>
                            <BreadcrumbLink to="/">Home</BreadcrumbLink>
                            <span>›</span>
                            <span>Movie Reviews</span>
                        </Breadcrumb>
                        <PageTitle>Movie Reviews</PageTitle>
                        <ArticleCount>{articles.length} review{articles.length !== 1 ? 's' : ''}</ArticleCount>
                    </HeaderInner>
                </PageHeader>

                <PageBody>
                    <BodyInner>
                        <AdWrap className="ad-container">
                            <Adsense.Google
                                client="ca-pub-5630104718741028"
                                slot="9699731559"
                                style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                format="auto"
                                responsive="true"
                            />
                        </AdWrap>

                        {articles.length > 0 ? (
                            <>
                                <Grid>
                                    {articles.slice(0, 8).map(({ node }) => (
                                        <ReviewItem key={node.uid} node={node} />
                                    ))}
                                </Grid>
                                {articles.length > 8 && (
                                    <AdWrap className="ad-container">
                                        <Adsense.Google
                                            client="ca-pub-5630104718741028"
                                            slot="9699731559"
                                            style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                            format="auto"
                                            responsive="true"
                                        />
                                    </AdWrap>
                                )}
                                {articles.length > 8 && (
                                    <Grid>
                                        {articles.slice(8).map(({ node }) => (
                                            <ReviewItem key={node.uid} node={node} />
                                        ))}
                                    </Grid>
                                )}
                            </>
                        ) : (
                            <EmptyState>No movie reviews found.</EmptyState>
                        )}
                    </BodyInner>
                </PageBody>
            </Layout>
        </>
    );
};

export default ReviewsPage;

export const pageQuery = graphql`
    query ReviewListingQuery {
        allPrismicReviewarticle(
            filter: { data: { active: { eq: "Y" } } }
            sort: { fields: [data___datetime], order: DESC }
            limit: 100
        ) {
            edges {
                node {
                    uid
                    data {
                        title { text }
                        img { url }
                        datetime
                        rating
                    }
                }
            }
        }
    }
`;
